// Where today's prices come from.
//
// The app never talks to The Odds API. `/api/odds` on this app's own server
// holds the key, fetches once a day for everyone, and keeps the day in storage
// (server/odds.mjs). The free tier is 500 credits a month; a key in the client
// would be a key in every download, and a fetch per device would spend the
// month's credits in an afternoon.
//
// The app keeps the day it was given, so a second open — or a plane — costs
// nothing and still prints the same four tickets.

import { apiBase } from './api-base.js';
import { prefs } from '../core/storage.js';
import {
  DEFAULT_PROVIDER, getProviderKey, getProviderSession, setProviderKey, keyHeaders, setProviderCredits,
} from './providers.js';
// The board's own join key, built here exactly as `fromOddsApiEvents` builds it
// — see `joinExchange`. `teams.js` is already on the boot path (store.js pulls
// `engine/universe.js` in statically), so this costs nothing.
import {
  matchKey as makeMatchKey, slug, sportFromOddsApiKey, teamId,
} from './engine/teams.js';

/** The AU region's bookmakers: The Odds API key, and the name a punter knows. */
export const BOOKMAKERS = [
  { key: 'sportsbet', name: 'Sportsbet' },
  { key: 'tab', name: 'TAB' },
  { key: 'ladbrokes_au', name: 'Ladbrokes' },
  { key: 'neds', name: 'Neds' },
  { key: 'pointsbetau', name: 'PointsBet' },
  { key: 'bet365_au', name: 'Bet365' },
  { key: 'unibet', name: 'Unibet' },
  { key: 'betr_au', name: 'Betr' },
  { key: 'tabtouch', name: 'TABtouch' },
  { key: 'playup', name: 'PlayUp' },
  { key: 'betright', name: 'Bet Right' },
  { key: 'dabble_au', name: 'Dabble' },
  { key: 'betfair_ex_au', name: 'Betfair Exchange' },
];

export const bookmakerName = (key) => BOOKMAKERS.find((b) => b.key === key)?.name ?? key;

/**
 * The boards this app ships knowing about, in the order they are shown.
 *
 * NO LONGER THE WHOLE CATALOGUE. GET /api/sports (server/sports.mjs) hands the
 * picker the provider's entire in-season list — seventy-odd leagues with a
 * head-to-head market — and that is the source of truth for what can be ticked.
 * These twelve are what the app knows with no network at all: the default set,
 * the names to print when the catalogue cannot be fetched, and the one field
 * upstream does not carry.
 *
 * `country` is HAND-CURATED and there is still nothing to replace it with: The
 * Odds API's /v4/sports returns a `group` ("Aussie Rules", "Soccer") and a
 * `description`, and no country field. It earns its keep in the picker as the
 * note beside a league's name rather than as the grouping — "Premier League" on
 * its own does not separate England's from Australia's.
 *
 * `group` is spelled the way The Odds API spells it, so one of these that has
 * fallen out of the live catalogue — out of season, and therefore absent — still
 * sorts under the heading it sat under all year.
 */
export const SPORT_CHOICES = [
  { key: 'aussierules_afl', name: 'AFL', country: 'Australia', group: 'Aussie Rules' },
  { key: 'rugbyleague_nrl', name: 'NRL', country: 'Australia', group: 'Rugby League' },
  { key: 'soccer_australia_aleague', name: 'A-League', country: 'Australia', group: 'Soccer' },
  { key: 'basketball_nbl', name: 'NBL', country: 'Australia', group: 'Basketball' },
  { key: 'cricket_big_bash', name: 'Big Bash', country: 'Australia', group: 'Cricket' },
  { key: 'rugbyunion_super_rugby', name: 'Super Rugby', country: 'Australia', group: 'Rugby Union' },
  { key: 'soccer_epl', name: 'Premier League', country: 'England', group: 'Soccer' },
  { key: 'basketball_nba', name: 'NBA', country: 'United States', group: 'Basketball' },
  { key: 'americanfootball_nfl', name: 'NFL', country: 'United States', group: 'American Football' },
  { key: 'baseball_mlb', name: 'MLB', country: 'United States', group: 'Baseball' },
  { key: 'icehockey_nhl', name: 'NHL', country: 'United States', group: 'Ice Hockey' },
  { key: 'soccer_uefa_champs_league', name: 'Champions League', country: 'Europe', group: 'Soccer' },
];

/** One of the twelve by key, or null — the fallback when the catalogue is away. */
export const knownSport = (key) => SPORT_CHOICES.find((s) => s.key === key) ?? null;

/**
 * A league's name for printing. The twelve have hand-written short names ("Big
 * Bash", not "Big Bash League"); anything else falls back to the key made
 * legible, which is only ever seen when a board was ticked from the catalogue
 * and the catalogue is now unreachable.
 */
export function sportName(key) {
  const known = knownSport(key);
  if (known) return known.name;
  const text = String(key ?? '').replace(/_/g, ' ').trim();
  return text ? text.replace(/\b[a-z]/g, (c) => c.toUpperCase()) : String(key ?? '');
}

/**
 * What a new install fetches: Australian sport, and nothing else. Anything
 * further afield is opted into one board at a time.
 *
 * Written out rather than derived from `country === 'Australia'`, because the
 * default set is a decision about what this app is for, and a hand edit to a
 * label in the catalogue above must not quietly change what everyone fetches.
 */
export const DEFAULT_SPORTS = [
  'aussierules_afl',
  'rugbyleague_nrl',
  'soccer_australia_aleague',
  'basketball_nbl',
  'cricket_big_bash',
  'rugbyunion_super_rugby',
];

const SPORTS_PREF = 'pref.sports';

/**
 * The most boards one fetch may name.
 *
 * MUST MATCH `MAX_REQUESTED_SPORTS` in server/odds.mjs. Past it the route
 * answers `bad-sports` — a 400, which this file reads as a plain `error` and
 * the pad prints as no board at all. The picker holds itself to the same number
 * so it can say what the limit is on the screen, rather than letting somebody
 * tick their way into an empty day and find out afterwards.
 */
export const MAX_SPORTS = 30;

/** The key shape the server will accept: `SPORT_KEY_RE` in server/odds.mjs. */
const SPORT_KEY_RE = /^[a-z0-9_]{1,50}$/;

/**
 * A list of keys as it will actually be stored: well-formed only, deduped, the
 * twelve known boards first in catalogue order and everything else after them
 * alphabetically, capped at MAX_SPORTS.
 *
 * THE TWELVE ARE NO LONGER A WHITELIST, and that is the change this function
 * exists to make. Anything outside SPORT_CHOICES used to be dropped here, which
 * is precisely what made a hardcoded list of twelve into a ceiling: a punter
 * could tick Serie A in a picker that offered it and the pref would quietly
 * decline to remember. What is checked now is the SHAPE of the key, because
 * that is all this file can know offline — and a key the server will not price
 * is ignored there anyway, which costs a slot in the list and nothing else.
 *
 * Order is cosmetic (the server sorts the keys before hashing them) but it is
 * stable, so the screen and the stored list always agree.
 */
export function normaliseSports(keys) {
  const wanted = [...new Set((Array.isArray(keys) ? keys : [])
    .filter((k) => typeof k === 'string' && SPORT_KEY_RE.test(k)))];
  const known = SPORT_CHOICES.map((s) => s.key).filter((k) => wanted.includes(k));
  const rest = wanted.filter((k) => !known.includes(k)).sort();
  return [...known, ...rest].slice(0, MAX_SPORTS);
}

/**
 * The boards this device counts, as Odds API keys.
 *
 * Nothing stored — and anything unusable stored — means the default set.
 */
export function getSports() {
  try {
    const kept = normaliseSports(prefs[SPORTS_PREF]);
    return kept.length ? kept : [...DEFAULT_SPORTS];
  } catch {
    return [...DEFAULT_SPORTS];
  }
}

/**
 * Store the ticked boards, and answer with what was actually stored. Ticking
 * nothing is not a choice — it would leave the pad with no prices to read — so
 * an empty set saves as the default one.
 */
export function setSports(keys) {
  const kept = normaliseSports(keys);
  const next = kept.length ? kept : [...DEFAULT_SPORTS];
  try { prefs[SPORTS_PREF] = next; } catch { /* private mode */ }
  return next;
}

/** What QA asked for (`empty`, `populated`, `default`), read once like core/demo.js does. */
export const qaSeed = (() => {
  try {
    return localStorage.getItem('appfactory:qa:seed');
  } catch {
    return null;
  }
})();

/**
 * The signed-in punter's token as a header, or nothing.
 *
 * The board is fetched once with somebody's key and handed to everyone who
 * asks, so the server asks who is asking. Imported late, so a build with the
 * auth addon switched off still runs.
 */
async function authHeaders() {
  try {
    const mod = await import('../../addons/auth/index.js');
    const token = await mod.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

const CACHE = 'oddsDay';

/** The day already on this device, if it is the day asked for. */
export function cachedSnapshot(date) {
  const s = prefs[CACHE];
  return s && s.forDate === date ? s : null;
}

/**
 * Ask the server for a day.
 *
 * Resolves, never rejects: { snapshot, status } where status is
 *   ready         the server answered (events may be empty before the drop)
 *   unconfigured  the server has no odds key yet
 *   offline       no network
 *   too-soon      a refresh inside the server's floor; `retryAt` says when
 *   error         the server answered with something else
 *
 * `refresh` asks the server to FETCH the prices again rather than hand back the
 * day it already has. It spends credits, so nothing calls it on a timer — see
 * updatePrices() in store.js, which is wired to a button that says the number.
 *
 * Snapshot: { forDate, fetchedAt, dropsAt?, events: [Odds API event] }.
 * Only a day with events is kept; an empty one is asked for again next open,
 * which costs nothing because the server has it cached too.
 */
/**
 * The Odds API key. Both of these are now wrappers over the provider map in
 * lib/providers.js — the single key was the whole of Settings for one release,
 * and a lot of the app still asks for it by this name. providers.js migrates
 * the old pref at import, so a device that had one still has one.
 */
export function getOddsApiKey() {
  try { return getProviderKey(DEFAULT_PROVIDER); } catch { return ''; }
}

export function setOddsApiKey(key) {
  setProviderKey(DEFAULT_PROVIDER, key);
}

export function getOddsCredits() {
  try { return prefs['pref.oddsCredits'] || null; } catch { return null; }
}

export function setOddsCredits(credits) {
  if (credits && (credits.remaining !== null || credits.used !== null)) {
    prefs['pref.oddsCredits'] = credits;
  }
}

export async function fetchSnapshot(date, {
  provider = DEFAULT_PROVIDER, sports = getSports(), refresh = false,
} = {}) {
  let res;
  // Every connected key goes up, because the server picks one by `?provider=`
  // and a request that carries the wrong one is a round trip wasted.
  const headers = { Accept: 'application/json', ...keyHeaders(), ...(await authHeaders()) };
  const query = new URLSearchParams({ date });
  if (provider !== DEFAULT_PROVIDER) query.set('provider', provider);
  // Always name the boards. This used to stay silent when the ticked set was
  // the same SIZE as the hardcoded catalogue, on the reasoning that asking for
  // all twelve gets the same board as asking for nothing. The catalogue is the
  // provider's whole in-season list now, so "twelve of them" says nothing at
  // all about whether those twelve are everything the server would fetch — and
  // the collapse was never this file's to make anyway: `sportsPlan` in
  // server/odds.mjs already answers a request naming exactly the ODDS_SPORTS
  // ceiling with the shared, untagged day. One rule, on the side that knows
  // what the ceiling is.
  if (sports.length) query.set('sports', sports.join(','));
  if (refresh) query.set('refresh', '1');
  try {
    res = await fetch(`${apiBase()}/api/odds?${query}`, { headers });
  } catch {
    return { snapshot: null, status: 'offline' };
  }
  // Two different 401s now. One means "this device has no odds key"; the other
  // means "the server does not know who you are". Telling somebody to paste an
  // API key when what they need is to sign in sends them the wrong way, so the
  // body decides which it was.
  if (res.status === 401) {
    let why = null;
    try { why = (await res.clone().json())?.error ?? null; } catch { why = null; }
    return { snapshot: null, status: why === 'sign-in-required' ? 'signed-out' : 'unconfigured' };
  }
  if (res.status === 503) return { snapshot: null, status: 'unconfigured' };
  // The refresh floor, which is a wait rather than a failure: the board was
  // fetched minutes ago and `retryAt` is when it may be fetched again.
  if (res.status === 429) {
    let body = null;
    try { body = await res.clone().json(); } catch { body = null; }
    if (body?.error === 'refresh-too-soon') {
      return { snapshot: null, status: 'too-soon', retryAt: body.retryAt ?? null };
    }
  }
  // `refresh-not-today` lands here as a plain 400 and reads as `error`. It is a
  // bug in the caller, not something to explain to a punter: the button that
  // asks is only ever offered on a board for today.
  if (!res.ok || !/json/.test(res.headers.get('content-type') ?? '')) {
    return { snapshot: null, status: 'error' };
  }
  try {
    const snapshot = await res.json();
    await addExchange(snapshot, date, sports);
    if (snapshot?.events?.length) prefs[CACHE] = snapshot;
    if (snapshot?.credits) {
      setOddsCredits(snapshot.credits);
      setProviderCredits(provider, snapshot.credits);
    }
    return { snapshot, status: 'ready' };
  } catch {
    return { snapshot: null, status: 'error' };
  }
}

/**
 * The Betfair Exchange's markets, folded into the board that just arrived.
 *
 * IT IS THE SAME BOARD, not a second one. The exchange bills nothing, so its
 * events ride in on the fetch the punter already made and `buildUniverse`
 * folds them onto the matchKey the Odds API's events already have — one more
 * column on a match that is there, rather than a board of its own to keep, to
 * cache and to go stale. The Odds API credit count is untouched either way.
 *
 * Only ever ADDED to a board that already has matches on it. Before the drop
 * the day is deliberately empty, and an empty day plus the exchange would be a
 * pad of matches only one book is pricing — which every strategy that needs a
 * consensus would then refuse, silently.
 *
 * ON THE PHONE THIS IS A DIRECT CALL TO BETFAIR (lib/betfair/transport.js,
 * over CapacitorHttp): Betfair's edge refuses Google Cloud addresses, so
 * Legwork's own server cannot fetch the exchange at all. On the web there is
 * no session to have — the login that would make one is refused — so the guard
 * below is what keeps this from firing there. Either way it costs no Odds API
 * credits and never runs on a timer: it rides the board fetch and nothing else.
 *
 * Imported late so a phone with no Betfair key never loads the module at all:
 * the boot path is already the heaviest thing in the app.
 */
async function addExchange(snapshot, date, sports) {
  if (!snapshot?.events?.length) return;
  if (!getProviderKey('betfair-exchange') || !getProviderSession('betfair-exchange')) return;
  try {
    const { betfairEvents } = await import('./betfair.js');
    const events = joinExchange(snapshot.events, await betfairEvents(date, sports));
    if (events.length) snapshot.events = [...snapshot.events, ...events];
  } catch {
    // The board is worth more than the extra column.
  }
}

/**
 * The exchange's events that have a TWIN ON THE BOARD, and only those.
 *
 * THE EXCHANGE ADDS COLUMNS; IT DOES NOT ADD MATCHES. `betfairEvents` asks
 * Betfair for a whole event type — Cricket is `4`, which is every cricket
 * market in the world — so what comes back covers the punter's leagues and a
 * great deal else besides: Budapest Blinders, Sierra Leone v Uganda, an English
 * county T20. Appended blind, each of those became a match on the board whose
 * only two prices were "Betfair Ex…" on either side. They can never be a leg
 * (every consensus wants at least three books and they have one), they can
 * never be in a bet, and they put a Cricket chip on Matches counting fixtures
 * no bookmaker in this app is pricing. The owner read that as the app being
 * broken, and he was right: it was a board with rows nothing could use.
 *
 * So the exchange joins, it never appends. The key is the board's own
 * `matchKey`, built here EXACTLY as `fromOddsApiEvents` (engine/universe.js)
 * builds it — same `sportFromOddsApiKey`, same `teamId`-then-`slug`, same
 * `matchKey` — because the whole reason a Betfair market and an Odds API event
 * fold into one Match downstream is that the two agree on that string. A second
 * spelling of it here would drop every exchange event silently.
 *
 * `protocol.js`'s `toEvents` has already done the hard half: it attributes a
 * market to a league through `isKnownTeam` and drops what it cannot place, so
 * what arrives is shaped like an Odds API event with `home_team`/`away_team`/
 * `sport_key` on it. All that is left is asking whether the board has that
 * fixture — and this is the right place to ask, because `buildUniverse` folds
 * by key without knowing which provider a row came from, and by the time it
 * looks the phantom is already a match.
 *
 * `src/lib/racing/exchange.js` joins the card the same way (on venue, race
 * number, code and jump time) for the same reason; this is the board's half.
 *
 * Pure: no network, no prefs, no clock. Answers a new array and mutates
 * nothing. An empty board answers `[]` — an exchange-only pad is a pad of
 * matches one book prices, which is the case this whole function is about.
 */
export function joinExchange(boardEvents, exchangeEvents, { log = console } = {}) {
  const board = new Set();
  for (const event of boardEvents ?? []) {
    const key = eventMatchKey(event);
    if (key) board.add(key);
  }
  if (!board.size) return [];

  const kept = [];
  const dropped = [];
  for (const event of exchangeEvents ?? []) {
    const key = eventMatchKey(event);
    if (key && board.has(key)) kept.push(event);
    else dropped.push(event);
  }

  if (dropped.length) {
    // A COUNT AND A SHAPE-CHECKED KEY, and nothing else off the payload. Team
    // names would make the line longer without making it more useful, and the
    // house rule for anything written to a log is that nothing from a provider
    // goes into it unscrubbed. `SPORT_KEY_RE` is that scrub: a sport key is
    // lowercase, digits and underscores, so a token cannot be wearing one.
    const named = [...new Set(dropped
      .map((e) => String(e?.sport_key ?? ''))
      .filter((k) => SPORT_KEY_RE.test(k)))].slice(0, 3);
    if (named.length < new Set(dropped.map((e) => e?.sport_key)).size) named.push('…');
    log.warn?.(`odds: ${dropped.length} exchange event${dropped.length === 1 ? '' : 's'} with no match on the board${named.length ? ` (${named.join(', ')})` : ''}`);
  }
  return kept;
}

/**
 * One Odds API event's `matchKey`, or null where it cannot have one.
 *
 * `fromOddsApiEvents`'s recipe, line for line. The two guards are this
 * function's own: an event with no readable kick-off or no two sides would key
 * on an invalid date and an empty id, and two of those would join to each other
 * — a phantom match built out of two events neither of which is a fixture.
 */
function eventMatchKey(event) {
  const home = String(event?.home_team ?? '').trim();
  const away = String(event?.away_team ?? '').trim();
  if (!home || !away) return null;
  if (!Number.isFinite(Date.parse(event?.commence_time ?? ''))) return null;
  const sport = sportFromOddsApiKey(event.sport_key) ?? event.sport_key;
  const homeId = teamId(sport, home) ?? slug(home);
  const awayId = teamId(sport, away) ?? slug(away);
  return makeMatchKey(sport, event.commence_time, homeId, awayId);
}

/**
 * Every league the provider will price: `{ sports, status }`, where a sport is
 * `{ key, title, group, description }` and status is one of the strings
 * `fetchSnapshot` uses — `ready`, `unconfigured`, `offline`, `error`.
 *
 * THIS COSTS NOTHING. /v4/sports is free on The Odds API and the server holds
 * the answer for six hours on top (server/sports.mjs), so the board picker can
 * show seventy leagues without opening Settings costing a credit. That is the
 * whole reason the picker can be a catalogue rather than a hardcoded twelve.
 *
 * Resolves, never rejects: a screen that cannot reach the catalogue falls back
 * to SPORT_CHOICES, which is a worse picker rather than a broken one.
 */
export async function fetchSportsCatalogue({ provider = DEFAULT_PROVIDER } = {}) {
  const headers = { Accept: 'application/json', ...keyHeaders(), ...(await authHeaders()) };
  const query = new URLSearchParams();
  if (provider !== DEFAULT_PROVIDER) query.set('provider', provider);
  const qs = query.toString();

  let res;
  try {
    res = await fetch(`${apiBase()}/api/sports${qs ? `?${qs}` : ''}`, { headers });
  } catch {
    return { sports: [], status: 'offline' };
  }
  if (res.status === 401) return { sports: [], status: 'unconfigured' };
  if (!res.ok || !/json/.test(res.headers.get('content-type') ?? '')) {
    return { sports: [], status: 'error' };
  }
  try {
    const body = await res.json();
    const sports = (Array.isArray(body?.sports) ? body.sports : [])
      .filter((s) => s && typeof s.key === 'string' && s.key);
    return { sports, status: 'ready' };
  } catch {
    return { sports: [], status: 'error' };
  }
}
